import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, HardHat, TrendingUp, Users, Award, Home as HomeIcon, CheckCircle2, Calendar } from 'lucide-react';
import { HeroSlider } from '../components/site/HeroSlider';
import { HeroSliderV2 } from '../components/site/HeroSliderV2';
import { HeroSliderV3 } from '../components/site/HeroSliderV3';
import { HeroSliderV4 } from '../components/site/HeroSliderV4';
import { HeroSliderV5 } from '../components/site/HeroSliderV5';
import { HeroSliderV6 } from '../components/site/HeroSliderV6';
import { ProjectNumberCard } from '../components/site/ProjectNumberCard';
import { SectionHeader } from '../components/site/SectionHeader';
import { SEOHead } from '../components/site/SEOHead';
import api, { getFileUrl } from '../lib/api';

const STAT_ICONS = {
  building: Building2, hardhat: HardHat, trending: TrendingUp,
  users: Users, award: Award, home: HomeIcon, check: CheckCircle2, calendar: Calendar,
};

const DEFAULT_STATS = {
  enabled: true,
  items: [
    { number: '20+', label: 'Yıllık Tecrübe', icon: 'building' },
    { number: '50+', label: 'Tamamlanan Proje', icon: 'hardhat' },
    { number: '100%', label: 'Müşteri Memnuniyeti', icon: 'trending' },
    { number: '10+', label: 'Devam Eden Proje', icon: 'building' },
  ],
};

const DEFAULT_CTA = {
  enabled: true,
  title: 'Hayalinizdeki Projeyi Birlikte Gerçekleştirelim',
  subtitle: 'Profesyonel ekibimizle iletişime geçin',
  button_text: 'İletişime Geçin',
  button_link: '/iletisim',
};

export default function Home({ onVersionDetected }) {
  const [sliders, setSliders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [menuConfig, setMenuConfig] = useState(null);
  const [corporate, setCorporate] = useState(null);
  const [seo, setSeo] = useState({});
  const [homepageVersion, setHomepageVersion] = useState('v1');
  const [stats, setStats] = useState(DEFAULT_STATS);
  const [cta, setCta] = useState(DEFAULT_CTA);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/sliders?active_only=true'),
      api.get('/projects?featured=true'),
      api.get('/settings/corporate'),
      api.get('/seo/home'),
      api.get('/settings/footer'),
      api.get('/blog/posts?published_only=true'),
      api.get('/settings/menu')
    ]).then(([s, p, c, seoRes, footerRes, blogRes, menuRes]) => {
      setSliders(s.data);
      setProjects(p.data);
      setCorporate(c.data);
      setSeo(seoRes.data);
      setBlogPosts(Array.isArray(blogRes.data) ? blogRes.data.slice(0, 9) : []);
      const md = { ...menuRes.data }; delete md.key; setMenuConfig(md);
      const version = footerRes.data?.homepage_version || 'v1';
      setHomepageVersion(version);
      if (onVersionDetected) onVersionDetected(version);
    }).catch(() => {}).finally(() => setLoading(false));
    // Stats are optional/per-tenant; fetch separately with default fallback
    api.get('/settings/stats')
      .then(r => {
        const d = r.data || {};
        setStats({
          enabled: d.enabled !== false,
          items: Array.isArray(d.items) ? d.items : DEFAULT_STATS.items,
        });
      })
      .catch(() => setStats(DEFAULT_STATS));
    api.get('/settings/cta')
      .then(r => {
        const d = r.data || {};
        setCta({
          enabled: d.enabled !== false,
          title: d.title || DEFAULT_CTA.title,
          subtitle: d.subtitle ?? DEFAULT_CTA.subtitle,
          button_text: d.button_text || DEFAULT_CTA.button_text,
          button_link: d.button_link || DEFAULT_CTA.button_link,
        });
      })
      .catch(() => setCta(DEFAULT_CTA));
  }, [onVersionDetected]);

  const isV2 = homepageVersion === 'v2';
  const isTransparentNav = homepageVersion === 'v2' || homepageVersion === 'v4';

  const renderHero = () => {
    switch (homepageVersion) {
      case 'v2': return <HeroSliderV2 slides={sliders} />;
      case 'v3': return <HeroSliderV3 slides={sliders} />;
      case 'v4': return <HeroSliderV4 slides={sliders} />;
      case 'v5': return <HeroSliderV5 projects={projects} />;
      case 'v6': return <HeroSliderV6 slides={sliders} />;
      default: return <HeroSlider slides={sliders} />;
    }
  };

  return (
    <div data-testid="home-page">
      <SEOHead title={seo.title} description={seo.description} keywords={seo.keywords} />
      
      {/* Hero - version based */}
      {renderHero()}

      {/* About Preview */}
      {corporate && (
        <section className="py-14 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
              <div className="md:col-span-7">
                <SectionHeader title={corporate.about_title || 'Hakkımızda'} />
                <p className="text-sm md:text-base leading-relaxed text-muted-foreground mb-6 line-clamp-6">
                  {corporate.about_text}
                </p>
                <Link
                  to="/kurumsal"
                  className="inline-flex items-center gap-2 text-sm font-medium text-[hsl(var(--gold))] hover:gap-3 transition-all duration-200"
                  data-testid="home-about-read-more"
                >
                  Devamını Oku <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="md:col-span-5">
                <div className="relative rounded-2xl overflow-hidden shadow-[var(--shadow-elev-2)]">
                  <img
                    src={corporate.image_path ? api.defaults.baseURL?.replace('/api', '') + '/api/files/' + corporate.image_path : 'https://images.pexels.com/photos/8961133/pexels-photo-8961133.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'}
                    alt="Kurumsal"
                    className="w-full aspect-[4/3] object-cover"
                  />
                  <div className="absolute inset-0 border-2 border-[hsl(var(--gold)/0.2)] rounded-2xl" />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Stats */}
      {stats.enabled && Array.isArray(stats.items) && stats.items.length > 0 && (
        <section className="py-14 md:py-20 bg-secondary" data-testid="home-stats-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.items.map((it, i) => {
                const Ic = STAT_ICONS[it.icon] || Building2;
                return <StatCard key={i} icon={<Ic />} number={it.number} label={it.label} />;
              })}
            </div>
          </div>
        </section>
      )}

      {/* Featured Projects */}
      {projects.length > 0 && (
        <section className="py-14 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="flex items-end justify-between mb-8 md:mb-12">
              <SectionHeader title="Devam Eden Projelerimiz" subtitle="En son projelerimize göz atın" className="mb-0" />
              <Link
                to="/projeler/devam-eden"
                className="hidden md:inline-flex items-center gap-2 text-sm font-medium text-[hsl(var(--gold))] hover:gap-3 transition-all duration-200"
              >
                Tüm Projeler <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.isArray(projects) && projects.slice(0, 3).map((project, i) => (
                <ProjectNumberCard key={project.id} project={project} index={i} />
              ))}
            </div>
            <div className="mt-8 text-center md:hidden">
              <Link
                to="/projeler/devam-eden"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] font-medium text-sm"
              >
                Tüm Projeler <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Haberler / Blog Section */}
      {menuConfig && menuConfig.blog !== false && blogPosts.length > 0 && (
        <section className="py-14 md:py-20 bg-secondary">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="flex items-end justify-between mb-8 md:mb-12">
              <SectionHeader title="Haberler" subtitle="En son haberler ve duyurular" className="mb-0" />
              <Link to="/blog" className="hidden md:inline-flex items-center gap-2 text-sm font-medium text-[hsl(var(--gold))] hover:gap-3 transition-all duration-200">
                Tüm Haberler <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {blogPosts.slice(0, 9).map(post => (
                <Link key={post.id} to={`/blog/${post.slug}`} className="group rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)] overflow-hidden hover:border-[hsl(var(--gold)/0.45)] transition-colors">
                  <div className="aspect-[16/9] overflow-hidden bg-secondary">
                    {post.cover_image ? (
                      <img src={getFileUrl(post.cover_image)} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">Görsel yok</div>
                    )}
                  </div>
                  <div className="p-4">
                    {post.category_name && <span className="text-xs font-medium text-[hsl(var(--gold))]">{post.category_name}</span>}
                    <h3 className="font-heading font-semibold text-sm mt-1 mb-1.5 line-clamp-2">{post.title}</h3>
                    {post.excerpt && <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{post.excerpt}</p>}
                    <span className="text-[11px] text-muted-foreground">{post.created_at ? new Date(post.created_at).toLocaleDateString('tr-TR') : ''}</span>
                  </div>
                </Link>
              ))}
            </div>
            <div className="mt-8 text-center md:hidden">
              <Link to="/blog" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] font-medium text-sm">
                Tüm Haberler <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Banner */}
      {cta.enabled && (cta.title || cta.subtitle || cta.button_text) && (
        <section className="py-14 md:py-20 bg-[#0f1419]" data-testid="home-cta-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
            {cta.title && (
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-4">
                {cta.title}
              </h2>
            )}
            {cta.subtitle && (
              <p className="text-gray-300 mb-8 max-w-xl mx-auto">
                {cta.subtitle}
              </p>
            )}
            {cta.button_text && (
              /^https?:\/\//i.test(cta.button_link) ? (
                <a
                  href={cta.button_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-8 py-3.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] font-medium hover:bg-[hsl(var(--gold)/0.92)] active:scale-[0.98] transition-colors duration-200"
                  data-testid="home-cta-button"
                >
                  {cta.button_text}
                </a>
              ) : (
                <Link
                  to={cta.button_link || '/iletisim'}
                  className="inline-flex items-center px-8 py-3.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] font-medium hover:bg-[hsl(var(--gold)/0.92)] active:scale-[0.98] transition-colors duration-200"
                  data-testid="home-cta-button"
                >
                  {cta.button_text}
                </Link>
              )
            )}
          </div>
        </section>
      )}
    </div>
  );
}

const StatCard = ({ icon, number, label }) => (
  <div className="text-center p-5 md:p-6 rounded-xl bg-card border border-border">
    <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[hsl(var(--gold)/0.1)] text-[hsl(var(--gold))] mb-3">
      {React.cloneElement(icon, { className: 'w-5 h-5' })}
    </div>
    <div className="font-heading text-2xl md:text-3xl font-semibold tabular-nums text-[hsl(var(--gold))]">{number}</div>
    <div className="text-xs text-muted-foreground mt-1">{label}</div>
  </div>
);
